import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexCol,
  FlexBetween,
  FlexRowCenterVertical,
} from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { Input } from '../General/Input';
import { TableTd, TableRow } from '../General/Table';
import { ProductPriceCartTable } from './ProductPriceCartTable';
import { getProductDefaultInfo } from '../../../utilities/selectors';
import { StockInfo } from './StockInfo';
import { OrderNrInput } from './OrderNrInput';
import { getSanitasProductDefaultInfo } from '../selectors';
import { TagType, Tag } from './Tag';
import { FreeDelivery } from './FreeDelivery';
import { SvgIcon, IconType } from '../General/SvgIcon';

export interface Props {
  product: any;
  addToRequest: any;
  addToCart: any;
  user?: any;
  handleOrderNrChange: (goodId, value) => void;
  showPackage?: boolean;
  isInCart: boolean;
  theme: any;
}

function ProductTabRowTable({
  product,
  addToCart,
  addToRequest,
  user,
  handleOrderNrChange,
  showPackage,
  isInCart,
  theme,
}: Props) {
  const {
    picture,
    url,
    name,
    goodId,
    pictureBig,
    issaleout,
    is_new,
    issale,
    currency,
    issaleb2b,
    orderAvailable,
    availability,
  } = getProductDefaultInfo(product);
  const {
    unit,
    onStock,
    price,
    priceWithoutVat,
    storesInfo,
    orderNr,
    productPackages,
    statusB2B,
    statusB2C,
    specialDiscountPcn,
    specialDiscount,
    specialDiscountWithoutVat,
    freeDelivery,
  } = getSanitasProductDefaultInfo(product);
  const isB2b = user && user.b2b;
  const statusShowPrice = isB2b ? statusB2B !== 3 : statusB2C !== 3;
  const statusShowAddToCart = isB2b ? statusB2B === 0 : statusB2C === 0;
  const correctPrice = statusShowPrice
    ? isB2b
      ? (+priceWithoutVat).toFixed(2)
      : (+price).toFixed(2)
    : '';
  const noStock = !statusShowAddToCart || !isB2b ? +onStock === 0 : false;

  const correctShownSpecialDiscount =
    specialDiscountPcn && specialDiscountPcn !== 0
      ? specialDiscountPcn
      : isB2b
      ? specialDiscountWithoutVat
      : specialDiscount;
  const correctShownSpecialDiscountText =
    correctShownSpecialDiscount && correctShownSpecialDiscount !== 0
      ? specialDiscountPcn && specialDiscountPcn
        ? `-${correctShownSpecialDiscount}%`
        : `${correctShownSpecialDiscount}${currency}`
      : '';
  let oldPriceCorrect = onStock > 0 ? (isB2b ? +priceWithoutVat : +price) : 0;
  oldPriceCorrect = correctShownSpecialDiscountText.includes('%')
    ? oldPriceCorrect / ((100 - correctShownSpecialDiscount) / 100)
    : correctShownSpecialDiscountText.includes(currency)
    ? oldPriceCorrect - +correctShownSpecialDiscount
    : 0;

  return (
    <>
      <ImageNameTableTd hasBubble={correctShownSpecialDiscountText}>
        <ImageWrapper to={url}>
          <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
          {isInCart && (
            <SvgIcon
              width={16}
              height={16}
              icon={IconType.cart}
              fill={theme.colors.primary}
              marginLeft={8}
              alt={__('Produkt sa nachádza v košíku')}
              title={__('Produkt sa nachádza v košíku')}
            />
          )}
        </ImageWrapper>
        <BigImage
          alt={`Zväčšený obrázok ku produktu ${name}`}
          src={pictureBig}
        />
        <NameInputWrapper>
          <Link to={url}>
            <Name>{name}</Name>
          </Link>
          <FlexRowCenterVertical>
            {user && (
              <OrderNrInput
                goodId={goodId}
                defaultValue={orderNr}
                handleChange={handleOrderNrChange}
              />
            )}
            {freeDelivery && (
              <FreeDelivery
                style={{
                  justifyContent: 'flex-start',
                  marginLeft: user ? rem(8) : 0,
                }}
              />
            )}
          </FlexRowCenterVertical>
        </NameInputWrapper>
      </ImageNameTableTd>
      <StyledTableTd>
        <TagWrapper>
          {is_new ? (
            <Tag
              style={{ marginRight: rem(4) }}
              text={'Novinka'}
              type={TagType.NEW}
            />
          ) : null}
          {issaleout ? (
            <Tag
              style={{ marginRight: rem(4) }}
              text={'Výpredaj'}
              type={TagType.SALEOUT}
            />
          ) : null}
          {(!isB2b && issale) || (isB2b && issaleb2b) ? (
            <Tag
              style={{ marginRight: rem(4) }}
              text={'Akcia'}
              type={TagType.ACTION}
            />
          ) : null}
        </TagWrapper>
      </StyledTableTd>
      <StyledTableTd>
        <StockInfo
          onStock={onStock}
          unit={unit}
          orderAvailable={orderAvailable}
          availability={availability}
        />
      </StyledTableTd>
      {showPackage && (
        <PackageTableTd>{`${productPackages} ${unit}`}</PackageTableTd>
      )}
      <ProductPriceCartTable
        price={correctPrice}
        oldPrice={oldPriceCorrect}
        unit={unit}
        addToRequest={addToRequest}
        addToCart={addToCart}
        noStock={noStock}
        isInCart={isInCart}
      />
    </>
  );
}

const StyledTableTd = styled(TableTd)`
  padding: ${rem(24)} 0 ${rem(16)};
  border-bottom: ${({ theme }) => theme.borders.primary};
`;

const TagWrapper = styled(FlexRowCenterVertical)``;

const ImageNameTableTd = styled(StyledTableTd)<{ hasBubble: boolean }>`
  display: flex;
  align-items: center;
  height: ${({ hasBubble }) => (hasBubble ? rem(111) : 'initial')};
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(24)};
`;

const PackageTableTd = styled(TableTd)`
  padding: ${rem(24)} 0 ${rem(16)};
  border-bottom: ${({ theme }) => theme.borders.primary};
  font-family: ${({ theme }) => theme.font.tertiary};
  font-size: ${rem(12)};
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-right: ${rem(6)};
  white-space: nowrap;
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;
`;

const NameInputWrapper = styled(FlexCol)`
  ${({ theme }) => theme.mediab.l1050`
      max-width: ${rem(300)};
  `}

  ${({ theme }) => theme.mediab.l925`
      max-width: ${rem(250)};
  `}
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(12)};
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(400)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.l1300`
      max-width: ${rem(400)};
  `}

  ${({ theme }) => theme.mediab.l1150`
      max-width: ${rem(300)};
  `}
`;

const BigImage = styled.img`
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%,-50%);
    box-shadow: ${({ theme }) => theme.boxShadow.secondary};
    z-index: 100;
  width: ${rem(500)};
  height: ${rem(500)};
  /* display: none; */
  /* margin-top: ${rem(300)}; */
  pointer-events: none;
  border-radius: ${rem(8)};
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  -moz-transition: opacity 0.3s ease-in-out;
  -webkit-transition: opacity 0.3s ease-in-out;

  ${ImageWrapper}:hover ~ & {
    opacity: 1;
    visibility: visible;
  }

  ${({ theme }) => theme.mediab.l1400`
      left: 65%;
  `}
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const WithTheme: any = withTheme(ProductTabRowTable);

export { WithTheme as ProductTabRowTable, StyledTableRow };
